import { ColumnDef, OnChangeFn, SortingState } from "@tanstack/react-table";
import { EditIcon, EyeIcon, EyeOffIcon, TrashAltIcon } from "assets/icons";
import { Media } from "components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ItemsTable, TableItemActionsControl } from "ui";

import { PackListItem } from "../../packs.types";

import styles from "./PacksTable.module.scss";

interface PacksTableProps {
  packs: PackListItem[];
  isLoading: boolean;
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  onRowClick?: (pack: PackListItem) => void;
  onEdit: (pack: PackListItem) => void;
  onDelete: (pack: PackListItem) => void;
  onToggleActive: (pack: PackListItem) => void;
}

const PacksTable = ({
  packs,
  isLoading,
  sorting,
  setSorting,
  onRowClick,
  onEdit,
  onDelete,
  onToggleActive
}: PacksTableProps) => {
  const { t } = useTranslation();
  const [openedActionsIndex, setOpenedActionsIndex] = useState<number | null>(
    null
  );

  const columns: Array<ColumnDef<PackListItem, PackListItem[]>> = [
    {
      id: "title",
      accessorKey: "title",
      header: t("Pack"),
      size: 40,
      minSize: 40,
      cell: ({ row }) => {
        const thumb = row.original.preview ?? row.original.image;
        return (
          <div className={styles.packsTable__title}>
            {thumb ? (
              <Media className={styles.packsTable__thumb} mediaFile={thumb} />
            ) : (
              <div className={styles.packsTable__thumb} />
            )}
            <div>{row.original.title}</div>
          </div>
        );
      }
    },
    {
      id: "shortId",
      accessorKey: "shortId",
      header: t("Short ID"),
      size: 16,
      minSize: 16,
      cell: ({ row }) => row.original.shortId
    },
    {
      id: "price",
      accessorKey: "price",
      header: t("Price"),
      size: 14,
      minSize: 14,
      cell: ({ row }) => row.original.price.toLocaleString("en-US")
    },
    {
      id: "slotsCount",
      header: t("Slots"),
      size: 10,
      minSize: 10,
      enableSorting: false,
      cell: ({ row }) => row.original.slots?.length ?? 0
    },
    {
      id: "isActive",
      accessorKey: "isActive",
      header: t("Status"),
      size: 14,
      minSize: 14,
      cell: ({ row }) => (
        <span
          className={styles.packsTable__statusBadge}
          style={{
            backgroundColor: row.original.isActive ? "#16C350" : "#A8A8BB"
          }}
        >
          {row.original.isActive ? t("Active") : t("Inactive")}
        </span>
      )
    },
    {
      id: "actions",
      header: () => null,
      size: 6,
      minSize: 6,
      enableSorting: false,
      cell: ({ row }) => (
        <TableItemActionsControl
          rowIndex={row.index}
          openedItemOptionsIndex={openedActionsIndex}
          setOpenedItemOptionsIndex={setOpenedActionsIndex}
          actionsList={[
            {
              label: t("Edit"),
              Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
              onClick: () => onEdit(row.original)
            },
            {
              label: row.original.isActive ? t("Deactivate") : t("Activate"),
              Icon: row.original.isActive ? (
                <EyeOffIcon fill={"#090914"} width={18} height={18} />
              ) : (
                <EyeIcon fill={"#090914"} width={18} height={18} />
              ),
              onClick: () => onToggleActive(row.original)
            },
            {
              label: t("Delete"),
              Icon: <TrashAltIcon fill={"#ff0033"} width={18} height={18} />,
              labelColor: "#ff0033",
              onClick: () => onDelete(row.original)
            }
          ]}
        />
      )
    }
  ];

  return (
    <ItemsTable
      columns={columns}
      data={packs}
      isLoading={isLoading}
      sorting={sorting}
      setSorting={setSorting}
      onRowClick={onRowClick}
      skeletonHeight={56}
    />
  );
};

export default PacksTable;
